/**
 * pathname → 메뉴 권한 해석.
 * 메뉴 urlPath(대표 목록)에 대해 longest-prefix로 상세·별칭 경로를 매칭한다.
 */
import type { MenuPermission } from '@/entities/auth/api/auth.api';
import type { AdminMenuTreeNode } from '@/entities/menu/types';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  canRead,
  canWrite,
} from '@/shared/utils/authPermissions';
import {
  CATALOG_PATH_BY_MENU_CODE,
  normalizeMenuUrlPath,
  ROUTE_PERMISSION_ALIASES,
} from '@/layouts/menuUrlRouteMap';
import {
  adminSidebarMenuItems,
  settingsSidebarMenuItems,
  type MenuGroup,
  type MenuLink,
  type SidebarMenuItem,
} from '@/layouts/sidebarMenuConfig';

export type MenuAccessState = {
  /** 매칭된 메뉴 키(서버 menuKey 또는 카탈로그 menuCode) */
  menuKey: string | null;
  /** urlPath 맵에 한 번이라도 매칭됐는지 */
  matched: boolean;
  canRead: boolean;
  canWrite: boolean;
  /** READ만 있을 때 — 폼·쓰기 버튼 비활성/숨김용 */
  readOnly: boolean;
};

/** 메뉴 권한 가드에서 제외 — 인증만으로 접근 */
export const MENU_ACCESS_EXEMPT_PATHS = new Set<string>([
  ROUTE_PATHS.MY_ACCOUNT,
  ROUTE_PATHS.DASHBOARD,
]);

const isMenuGroup = (item: SidebarMenuItem): item is MenuGroup =>
  !('type' in item) && 'children' in item;

const isMenuLink = (item: SidebarMenuItem): item is MenuLink =>
  !('type' in item) && 'key' in item && !('children' in item);

/** 트리의 urlPath → menuKey (visible 여부와 무관 — 권한 해석용) */
export const collectMenuKeyByUrlPath = (
  menus: AdminMenuTreeNode[] | undefined | null,
): Record<string, string> => {
  const map: Record<string, string> = {};

  const walk = (nodes: AdminMenuTreeNode[]) => {
    for (const node of nodes) {
      const path = normalizeMenuUrlPath(node.urlPath);
      if (path && node.menuKey) {
        map[path] = node.menuKey;
      }
      if (node.children?.length) walk(node.children);
    }
  };

  if (menus?.length) walk(menus);
  return map;
};

/** `/admin/auth/me` permissions urlPath → menuCode (없으면 카탈로그 경로) */
export const collectMenuKeyByUrlPathFromPermissions = (
  permissions: MenuPermission[] | undefined | null,
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!permissions?.length) return map;

  for (const permission of permissions) {
    if (!permission.menuCode) continue;
    const path =
      normalizeMenuUrlPath(permission.urlPath) ??
      CATALOG_PATH_BY_MENU_CODE[permission.menuCode] ??
      null;
    if (path) map[path] = permission.menuCode;
  }
  return map;
};

/** 하드코딩 사이드바 path → menuCode (트리 폴백) */
export const collectCatalogMenuKeyByUrlPath = (): Record<string, string> => {
  const map: Record<string, string> = {};

  const ingest = (items: SidebarMenuItem[]) => {
    for (const item of items) {
      if ('type' in item) continue;
      if (isMenuGroup(item)) {
        for (const child of item.children) {
          const path = normalizeMenuUrlPath(child.key);
          if (path && child.menuCode) map[path] = child.menuCode;
        }
        continue;
      }
      if (isMenuLink(item)) {
        const path = normalizeMenuUrlPath(item.key);
        if (path && item.menuCode) map[path] = item.menuCode;
      }
    }
  };

  ingest(adminSidebarMenuItems);
  ingest(settingsSidebarMenuItems);
  return map;
};

/** 별칭 경로에 부모 목록 menuKey를 복사 */
export const applyPermissionPathAliases = (
  map: Record<string, string>,
): Record<string, string> => {
  const next = { ...map };
  for (const alias of ROUTE_PERMISSION_ALIASES) {
    const aliasPath = normalizeMenuUrlPath(alias.path);
    const fromPath = normalizeMenuUrlPath(alias.from);
    if (!aliasPath || !fromPath) continue;
    if (next[aliasPath]) continue;
    const parentKey = next[fromPath];
    if (parentKey) next[aliasPath] = parentKey;
  }
  return next;
};

/**
 * 권한 맵 구성: me.permissions urlPath 우선, 카탈로그 menuCode로 보완 후 별칭 적용.
 * 트리 인자는 메뉴 설정 화면 등 하위 호환용.
 */
export const buildMenuKeyByUrlPath = (
  menus: AdminMenuTreeNode[] | undefined | null,
  permissions?: MenuPermission[] | undefined | null,
): Record<string, string> => {
  const fromCatalog = collectCatalogMenuKeyByUrlPath();
  const fromTree = collectMenuKeyByUrlPath(menus);
  const fromPermissions = collectMenuKeyByUrlPathFromPermissions(permissions);
  const merged = { ...fromCatalog, ...fromTree, ...fromPermissions };
  return applyPermissionPathAliases(merged);
};

/** pathname → menuKey (가장 긴 urlPath 접두 일치) */
export const resolveMenuKeyByPath = (
  pathname: string,
  menuKeyByUrlPath: Record<string, string>,
): string | null => {
  const path = normalizeMenuUrlPath(pathname);
  if (!path) return null;

  const exact = menuKeyByUrlPath[path];
  if (exact) return exact;

  const candidates = Object.keys(menuKeyByUrlPath).sort(
    (a, b) => b.length - a.length,
  );
  for (const catalogPath of candidates) {
    if (path === catalogPath || path.startsWith(`${catalogPath}/`)) {
      return menuKeyByUrlPath[catalogPath] ?? null;
    }
  }
  return null;
};

export const isMenuAccessExemptPath = (pathname: string): boolean => {
  const path = normalizeMenuUrlPath(pathname);
  if (!path) return false;
  if (MENU_ACCESS_EXEMPT_PATHS.has(path)) return true;
  for (const exempt of MENU_ACCESS_EXEMPT_PATHS) {
    if (path.startsWith(`${exempt}/`)) return true;
  }
  return false;
};

export const resolveMenuAccessByPath = (options: {
  pathname: string;
  menuKeyByUrlPath: Record<string, string>;
  permissions: MenuPermission[] | undefined | null;
  role?: string | null;
}): MenuAccessState => {
  const { pathname, menuKeyByUrlPath, permissions, role } = options;

  if (isMenuAccessExemptPath(pathname)) {
    return {
      menuKey: null,
      matched: false,
      canRead: true,
      canWrite: true,
      readOnly: false,
    };
  }

  const menuKey = resolveMenuKeyByPath(pathname, menuKeyByUrlPath);
  if (!menuKey) {
    // 맵에 없는 경로 — 인증된 어드민이면 통과(미등록 화면 잠금 방지)
    return {
      menuKey: null,
      matched: false,
      canRead: true,
      canWrite: true,
      readOnly: false,
    };
  }

  const readable = canRead(permissions, menuKey, role);
  const writable = canWrite(permissions, menuKey, role);
  return {
    menuKey,
    matched: true,
    canRead: readable,
    canWrite: writable,
    readOnly: readable && !writable,
  };
};
